import React, { useCallback, useEffect, useMemo, useState } from "react"
import { ActivityIndicator } from "~/ui/primitives"
import styled from "~/ui/styled"
import ImagePicker from "~/ui/image-picker"
import useMediaLibrary from "~hooks/useMediaLibrary"
import { useStores } from "~models"
import Avatar from "./Avatar"
import { AvatarProps } from "./types"

type UploadResult = {
  uri: string
  path: string
}

export type UploadableAvatarProps = Omit<AvatarProps, "avatar" | "onClick"> & {
  /** 当前头像（远程 uri） */
  avatar?: string
  /** 上传成功回调 */
  onChange?: (result: UploadResult) => void
  /** 选择图片 & 上传过程中禁用点击 */
  disabled?: boolean
  /** 上传 mime type */
  uploadMimeType?: string
}

const Wrap = styled.View`
  position: relative;
`

const Overlay = styled.View<{ radius: number }>`
  position: absolute;
  left: 0px;
  top: 0px;
  right: 0px;
  bottom: 0px;
  border-radius: ${({ radius }) => radius}px;
  align-items: center;
  justify-content: center;
  background-color: rgba(236, 234, 234, 0.55);
`

function inferMimeType(path: string, fallback: string) {
  const p = path.toLowerCase()
  if (p.endsWith(".png")) return "image/png"
  if (p.endsWith(".webp")) return "image/webp"
  if (p.endsWith(".heic") || p.endsWith(".heif")) return "image/heic"
  if (p.endsWith(".jpg") || p.endsWith(".jpeg")) return "image/jpeg"
  return fallback
}

const UploadableAvatar: React.FC<UploadableAvatarProps> = ({
  avatar,
  icon,
  name,
  size = 40,
  color,
  type,
  squareRadius,
  backgroundColor,
  onChange,
  disabled,
  uploadMimeType = "image/jpeg",
}) => {
  const { apiStore } = useStores()
  const { requestPermissionsAsyncWithCallback } = useMediaLibrary({ writeOnly: false })
  const [uploading, setUploading] = useState(false)
  const [previewUri, setPreviewUri] = useState("")

  useEffect(() => {
    if (avatar) setPreviewUri("")
  }, [avatar])

  const displayAvatar = avatar || previewUri

  const radius = useMemo(() => {
    if (type === 0) return squareRadius ?? 6 // AvatarWrapperTypes.SQUARE
    return size / 2 // AvatarWrapperTypes.CIRCLE (default)
  }, [size, squareRadius, type])

  const onClick = useCallback(async () => {
    if (disabled || uploading) return

    await requestPermissionsAsyncWithCallback({
      callback: async () => {
        const { path } = await ImagePicker.openPicker({
          cropping: false,
          multiple: false,
          mediaType: "photo",
          loadingLabelText: "处理中...",
        })

        setPreviewUri(path)

        try {
          setUploading(true)
          const mimeType = inferMimeType(path, uploadMimeType)
          const { uri } = await apiStore.upload({ path, type: mimeType })
          onChange?.({ uri, path })
        } finally {
          setUploading(false)
        }
      },
    })
  }, [apiStore, disabled, onChange, requestPermissionsAsyncWithCallback, uploadMimeType, uploading])

  return (
    <Wrap>
      <Avatar
        avatar={displayAvatar}
        icon={icon}
        name={name}
        size={size}
        color={color}
        type={type}
        squareRadius={squareRadius}
        backgroundColor={backgroundColor}
        onClick={onClick}
      />
      {uploading ? (
        <Overlay radius={radius}>
          <ActivityIndicator color={"black"} />
        </Overlay>
      ) : null}
    </Wrap>
  )
}

export default React.memo(UploadableAvatar)

